/**
 * Environment and release resolution.
 *
 * Reads from an explicitly passed bag rather than `process.env`, because only
 * `NEXT_PUBLIC_*` is inlined into browser bundles and Plasmo uses
 * `PLASMO_PUBLIC_*` — a module that reached for `process.env` directly would
 * silently resolve to `undefined` on two of our four runtimes.
 */

/**
 * Free-form on purpose. `ENVIRONMENT` is already the repo's canonical
 * discriminator and is branch-scoped in Vercel to `prod`, `staging`, `dev2`,
 * `dev` and `preview` — remapping those onto a fixed union would silently
 * merge environments the team deliberately keeps apart (`dev2` into
 * `preview`, `release` into `staging`).
 */
export type DeploymentEnvironment = string;

export type EnvBag = Record<string, string | undefined>;

/**
 * Resolve `deployment.environment.name`, which becomes Logfire's
 * `deployment_environment` column and its environment selector.
 *
 * `ENVIRONMENT` is passed through verbatim. Only when it is absent do we fall
 * back to `VERCEL_ENV`, which is a poor substitute: it returns just
 * production | preview | development, so dev, dev2, staging and release all
 * collapse into "preview" and become indistinguishable.
 */
export function resolveEnvironment(env: EnvBag): DeploymentEnvironment {
  const explicit = (
    env.ENVIRONMENT ??
    env.LOGFIRE_ENVIRONMENT ??
    env.NEXT_PUBLIC_LOGFIRE_ENVIRONMENT ??
    env.PLASMO_PUBLIC_LOGFIRE_ENVIRONMENT
  )?.trim();

  if (explicit) return explicit.toLowerCase();

  const vercel = (env.VERCEL_ENV ?? env.NEXT_PUBLIC_VERCEL_ENV)?.toLowerCase();
  if (vercel === 'production') return 'prod';
  if (vercel === 'preview') return 'preview';
  return 'dev';
}

/**
 * `service.version`: a real version if we have one, else the short commit sha.
 *
 * Falls back to `local` rather than `dev` when off Vercel, so a developer's
 * machine is distinguishable at a glance — otherwise every laptop reports the
 * same version as the deployed `dev` branch and you cannot tell them apart.
 */
export function resolveRelease(env: EnvBag): string {
  const explicit = env.LOGFIRE_SERVICE_VERSION ?? env.NEXT_PUBLIC_LOGFIRE_SERVICE_VERSION ?? env.PLASMO_PUBLIC_APP_VERSION;
  if (explicit) return explicit;

  const sha = env.VERCEL_GIT_COMMIT_SHA ?? env.NEXT_PUBLIC_LOGFIRE_COMMIT_SHA;
  if (sha) return sha.slice(0, 7);

  // On Vercel with no sha is a deploy we cannot pin to a commit — worth
  // distinguishing from a laptop, since it points at a broken build config.
  return env.VERCEL ? 'unknown' : 'local';
}

/**
 * Whether this environment may carry raw request/response payloads on spans.
 *
 * Production never does — the strict-PII bar is absolute there, and the data is
 * real customer data. Everywhere else the payload is test data, and a full body
 * is worth far more than a shape summary when you are actually debugging.
 *
 * Gate at the point of emission, not in the scrubber: an attribute that is
 * never set cannot leak.
 */
export function capturesFullPayloads(env: EnvBag): boolean {
  return resolveEnvironment(env) !== 'prod';
}

/**
 * Logfire's US ingest base. Region is actually derived from the token prefix
 * (`pylf_v1_us_…`), so this only matters as the default endpoint we point the
 * exporter at; override with `LOGFIRE_BASE_URL` for EU or a collector.
 */
export const DEFAULT_LOGFIRE_BASE_URL = 'https://logfire-us.pydantic.dev';
